// ===========================================
// useScrollDirection Hook
// ===========================================
// Detects scroll direction for hiding/showing UI elements

import { useState, useEffect, useRef, useCallback } from 'react';

// ===========================================
// Constants
// ===========================================

/**
 * Default scroll delta threshold (in pixels) before triggering direction change.
 * Prevents jittery behavior from small scroll movements.
 */
const DEFAULT_SCROLL_THRESHOLD = 10;

/**
 * Default scroll position threshold (in pixels) to always show navigation.
 * When user is near the top of the page, navigation always remains visible.
 */
const DEFAULT_ALWAYS_SHOW_THRESHOLD = 100;

// ===========================================
// Types
// ===========================================

export type ScrollDirection = 'up' | 'down' | null;

interface UseScrollDirectionOptions {
  /** Minimum scroll delta before triggering direction change (default: 10) */
  threshold?: number;
  /** Scroll position threshold to always show (default: 100) */
  alwaysShowThreshold?: number;
}

interface UseScrollDirectionReturn {
  /** Current scroll direction */
  scrollDirection: ScrollDirection;
  /** Current scroll position */
  scrollY: number;
  /** Whether the element should be visible based on scroll direction */
  isVisible: boolean;
}

/**
 * Hook to detect scroll direction for hiding/showing bottom navigation
 * Returns 'up' when scrolling up, 'down' when scrolling down
 */
export function useScrollDirection(
  options: UseScrollDirectionOptions = {}
): UseScrollDirectionReturn {
  const {
    threshold = DEFAULT_SCROLL_THRESHOLD,
    alwaysShowThreshold = DEFAULT_ALWAYS_SHOW_THRESHOLD,
  } = options;

  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null);
  const [scrollY, setScrollY] = useState(0);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const updateScrollDirection = useCallback(() => {
    const currentScrollY = window.scrollY;

    // Always show if near top of page
    if (currentScrollY < alwaysShowThreshold) {
      setScrollDirection('up');
      setScrollY(currentScrollY);
      lastScrollY.current = currentScrollY;
      ticking.current = false;
      return;
    }

    // Calculate scroll delta
    const delta = currentScrollY - lastScrollY.current;

    // Only update if delta exceeds threshold
    if (Math.abs(delta) >= threshold) {
      const direction = delta > 0 ? 'down' : 'up';
      setScrollDirection(direction);
      lastScrollY.current = currentScrollY;
    }

    setScrollY(currentScrollY);
    ticking.current = false;
  }, [threshold, alwaysShowThreshold]);

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      window.requestAnimationFrame(updateScrollDirection);
      ticking.current = true;
    }
  }, [updateScrollDirection]);

  useEffect(() => {
    // Initialize with current scroll position
    lastScrollY.current = window.scrollY;
    setScrollY(window.scrollY);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Calculate visibility based on scroll direction
  const isVisible = scrollDirection !== 'down';

  return {
    scrollDirection,
    scrollY,
    isVisible,
  };
}

export default useScrollDirection;
